import { FC, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { FieldHelperProps, FieldInputProps, FieldMetaProps } from 'formik';
import { useTranslation } from 'react-i18next';
import { useLocation, useOutletContext } from 'react-router-dom';
import { InputNumberProps, Tooltip } from 'antd';
import { debounce, kebabCase } from 'lodash';
import { flushSync } from 'react-dom';
import IconTooltip from 'components/ui/base/tooltip/IconTooltip';
import { TextSmall } from 'components/ui/base/typography';
import useDeclarationInputFocused from 'hooks/useDeclarationInputFocused';
import {
    ErrorDiv,
    ErrorLabel,
    FieldWrapper,
    FormItem,
    InputDiv,
    InputLabel,
    InputLabelRequired,
    InputSection,
    LabelDiv,
    MetaSection,
    RefDiv,
    StyledNumberInput,
} from './DeclarationInput.styles';
import { getFormikProps, toPlaceholder } from 'views/declarations/utils/form-utils';
import MultipleButtons from '../MultipleButtons';
import { TemplateCheckboxes } from 'components/ui/composed/template/TemplateCheckbox';
import { useTemplateContext } from '../../template/TemplateContext';
import { useRegisterField } from '../../template/CardContext';
import useFieldTemplateMetaData from '../../template/useTemplateViewEditControls';
import useNonAmendableField from 'views/declarations/utils/useNonAmendableField';
import useDeclarations from 'hooks/useDeclarations';
import { TestProps } from 'core/utils/testTypes';
import { StyledInfoCircleOutlined } from 'views/declarations/declaration-tabs/declaration-form/Form.styles';
import { useDeclarationValueSource } from '../source/DeclarationSourceContext';
import DeclarationValueSourceIcon from '../source/DeclarationValueSourceIcon';

export interface FormNumberInputProps extends InputNumberProps, TestProps {
    label?: string;
    refTooltip?: string;
    tooltip?: string | false;
    icon?: ReactNode;
    refNumber?: string;
    children?: ReactNode;
    fieldProps?: FieldInputProps<any>;
    fieldMeta?: FieldMetaProps<any>;
    fieldHelper?: FieldHelperProps<any>;
    required?: boolean;
    maxLength?: number;
    viewOnly?: boolean;
    integerNumbers?: boolean;
    min?: string;
    max?: string;
    disabled?: boolean;
    stringMode?: boolean;
    refClicked?: (refNumber: string | number) => void;
    fieldEvents?: {
        onBlur?: (value: string | null) => void;
        onChange?: (value: number | null) => void;
    };
    value?: number;
    condensed?: boolean;
    multipleF?: {
        onAdd: () => void;
        onRemove: () => void;
        canAdd?: boolean;
        canRemove?: boolean;
    };
    specialName?: string; // Used for template meta handle of taxBox34Bis fields as of 13/04/23
}

const DeclarationNumberInput: FC<FormNumberInputProps> = ({
    label,
    refNumber,
    refTooltip,
    tooltip,
    fieldMeta: fieldMetaProp,
    fieldProps: fieldPropsProp,
    fieldHelper: fieldHelpersProp,
    required,
    viewOnly: viewOnlyProp,
    min = '0',
    disabled,
    refClicked,
    fieldEvents,
    value: valueProp,
    maxLength,
    condensed,
    multipleF,
    specialName,
    hidden,
    testId,
    ...numberInputProps
}) => {
    const { t } = useTranslation('common');
    const { setFocused } = useDeclarationInputFocused();
    const outletContext = useOutletContext<{
        amendment?: boolean;
    }>();
    const location = useLocation();
    const inViewOnly = location.pathname.split('/').includes('view-only');
    const { template, templateFormik, form, isViewOnly: isTemplateViewOnly } = useTemplateContext();
    const { declaration } = useDeclarations();

    const [tooltipVisible, setTooltipVisible] = useState(false);
    const showTooltip = useMemo(
        () =>
            debounce(() => {
                if (!tooltip) return;
                setTooltipVisible(true);
            }, 200),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );
    const hideTooltip = () => {
        if (!tooltip) return;
        showTooltip?.cancel();
        setTooltipVisible(false);
    };

    useRegisterField({ path: fieldPropsProp?.name, required, hidden });
    const { isViewable, isEditable, isInvisible } = useFieldTemplateMetaData(specialName ?? fieldPropsProp?.name);
    const isNonAmendable = useNonAmendableField(specialName ?? fieldPropsProp?.name);

    const { fieldProps, fieldMeta, fieldHelpers } = useMemo(() => {
        if (template && templateFormik && form) {
            const f = getFormikProps(`${form}.defaults.${fieldPropsProp?.name}`, templateFormik);
            return { ...f, fieldHelpers: f.fieldHelper };
        }

        return { fieldProps: fieldPropsProp, fieldMeta: fieldMetaProp, fieldHelpers: fieldHelpersProp };
    }, [template, templateFormik, form, fieldPropsProp, fieldMetaProp, fieldHelpersProp]);

    const [localValue, setLocalValue] = useState<number | undefined | null>(undefined);

    const { onBlur, ...otherFieldEvents } = fieldEvents ?? {};

    const handleRefClick = useCallback(() => {
        if (refClicked) {
            refNumber ? refClicked(refNumber) : refClicked(label ?? '');
        }
    }, [refClicked, refNumber, label]);

    useEffect(() => {
        if (hidden) return;
        if (fieldProps?.value === '' && fieldHelpers) {
            fieldHelpers.setValue(null);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fieldProps?.value]);

    const showRefTooltip = useMemo(() => refTooltip && refTooltip.length > 0, [refTooltip]);

    const viewOnly = useMemo(() => {
        if (template) return isTemplateViewOnly;
        if (outletContext?.amendment) return isViewable;
        return viewOnlyProp || !isEditable || inViewOnly || declaration?.archived;
    }, [
        template,
        isTemplateViewOnly,
        outletContext?.amendment,
        isViewable,
        viewOnlyProp,
        isEditable,
        inViewOnly,
        declaration?.archived,
    ]);

    const value = useMemo(() => {
        if (localValue !== undefined) return localValue;
        return valueProp ?? fieldProps?.value;
    }, [fieldProps?.value, localValue, valueProp]);

    const valueSource = useDeclarationValueSource(fieldPropsProp?.name, fieldMeta);
    const sourceIcon = useMemo(
        () => (valueSource ? <DeclarationValueSourceIcon {...valueSource} /> : undefined),
        [valueSource]
    );

    const handleTemplateCheckboxChange = useMemo(() => {
        return numberInputProps.onChange ?? otherFieldEvents.onChange;
    }, [numberInputProps.onChange, otherFieldEvents.onChange]);

    const render = useMemo(
        () => (
            <FieldWrapper $viewOnly={viewOnly} $template={template}>
                {viewOnly ? (
                    <>
                        <MetaSection>
                            <LabelDiv condensed={condensed}>
                                {refNumber && <RefDiv>{refNumber}</RefDiv>}
                                {label && (
                                    <InputLabel>
                                        {required && <InputLabelRequired right>*</InputLabelRequired>}
                                        {label}
                                    </InputLabel>
                                )}
                                {showRefTooltip && (
                                    <IconTooltip
                                        title={label}
                                        tooltip={refTooltip ?? t('defaultTooltip')}
                                        icon={<StyledInfoCircleOutlined />}
                                        tooltipClicked={handleRefClick}
                                    />
                                )}
                            </LabelDiv>
                            {template && <TemplateCheckboxes disabled fieldPath={fieldPropsProp?.name} />}
                        </MetaSection>
                        <InputSection>
                            <TextSmall data-testid={testId ?? kebabCase(label ?? fieldPropsProp?.name)}>
                                {fieldProps?.value ?? '-'}
                            </TextSmall>
                        </InputSection>
                    </>
                ) : (
                    <>
                        <MetaSection>
                            <LabelDiv condensed={condensed}>
                                {refNumber && <RefDiv>{refNumber}</RefDiv>}
                                {label && (
                                    <InputLabel>
                                        {required && <InputLabelRequired right>*</InputLabelRequired>}
                                        {label}
                                    </InputLabel>
                                )}
                                {showRefTooltip && (
                                    <IconTooltip
                                        title={label}
                                        tooltip={refTooltip ?? t('defaultTooltip')}
                                        icon={<StyledInfoCircleOutlined />}
                                        tooltipClicked={handleRefClick}
                                    />
                                )}
                            </LabelDiv>
                        </MetaSection>
                        <InputSection>
                            <FormItem validateStatus={fieldMeta?.error && !!fieldMeta?.touched ? 'error' : ''}>
                                <InputDiv>
                                    <Tooltip open={tooltipVisible} overlay={tooltip}>
                                        <StyledNumberInput
                                            lock={!!template}
                                            onMouseOver={showTooltip}
                                            onMouseOut={hideTooltip}
                                            maxLength={maxLength}
                                            placeholder={toPlaceholder(label) ?? ''}
                                            prefix={sourceIcon}
                                            name={fieldProps?.name}
                                            data-testid={testId ?? kebabCase(label ?? fieldPropsProp?.name)}
                                            min={min}
                                            disabled={disabled || isInvisible || isNonAmendable}
                                            status={fieldMeta?.error && !!fieldMeta?.touched ? 'error' : ''}
                                            multiple={fieldProps?.multiple}
                                            checked={fieldProps?.checked}
                                            {...fieldMeta}
                                            {...otherFieldEvents}
                                            size={condensed ? 'middle' : 'large'}
                                            condensed={condensed}
                                            {...numberInputProps}
                                            value={value}
                                            onFocus={(e) => {
                                                setFocused(true);
                                                setLocalValue(e.target.value ? Number(e.target.value) : null);
                                            }}
                                            onBlur={(e) => {
                                                const rawValue = e.target.value.trim();
                                                const num = Number(rawValue);
                                                const value = rawValue === '' || isNaN(num) ? null : String(num);

                                                flushSync(() => {
                                                    if (numberInputProps.onChange) numberInputProps?.onChange?.(value);
                                                    else if (otherFieldEvents.onChange)
                                                        otherFieldEvents?.onChange?.(value as any);
                                                    else
                                                        fieldProps?.onChange({
                                                            target: {
                                                                value: value || null,
                                                                name: fieldProps.name,
                                                            },
                                                        });
                                                });
                                                setFocused(false);
                                                setLocalValue(undefined);
                                                if (numberInputProps.onBlur) numberInputProps.onBlur(e);
                                                if (onBlur) onBlur?.(value);
                                                else fieldProps?.onBlur(e);
                                                fieldHelpers?.setTouched(true);
                                            }}
                                            onChange={(value: number | null) => setLocalValue(value)}
                                        />
                                    </Tooltip>
                                    {template && (
                                        <TemplateCheckboxes
                                            required={required}
                                            fieldPath={fieldPropsProp?.name}
                                            specialName={specialName}
                                            onChange={handleTemplateCheckboxChange}
                                            disabled={disabled}
                                        />
                                    )}
                                    {multipleF && <MultipleButtons addLeftMargin={!!template} {...multipleF} />}
                                </InputDiv>
                                <ErrorDiv
                                    error={!!fieldMeta?.error}
                                    touched={!!fieldMeta?.touched}
                                    condensed={condensed}
                                >
                                    {!!fieldMeta?.error && !!fieldMeta?.touched && (
                                        <ErrorLabel>{fieldMeta?.error}</ErrorLabel>
                                    )}
                                </ErrorDiv>
                            </FormItem>
                        </InputSection>
                    </>
                )}
            </FieldWrapper>
        ),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            fieldMeta?.error,
            fieldMeta?.touched,
            value,
            onBlur,
            disabled,
            localValue,
            viewOnly,
            multipleF?.canRemove,
            multipleF?.canAdd,
            numberInputProps,
            sourceIcon,
        ]
    );

    if (hidden || (isInvisible && !template)) return null;

    return render;
};

export default DeclarationNumberInput;
